import {
  describeSettings,
  isLobbySettings,
  type PendingMatch,
  type RegisterConnectorDetail,
  type RegisterConnectorInput,
  type RegisterConnectorSummary,
  type RegisterRunSummary,
  SCHEDULE_LABELS,
} from '@hoopoe/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, UserSearch } from 'lucide-react';
import { useState } from 'react';
import { useReauth } from '../components/auth/reauth';
import { DataNumber, DataText } from '../components/data/data-text';
import { EmptyState } from '../components/guidance/empty-state';
import { ErrorState } from '../components/guidance/error-state';
import { LearnMore } from '../components/guidance/learn-more';
import { PageHeader } from '../components/layout/app-shell';
import { EpDetails, LobbyDetails } from '../components/registers/connector-details';
import { ConnectorForm } from '../components/registers/connector-form';
import { ConnectorStatus, RUN_LABEL } from '../components/registers/connector-status';
import { LobbyForm } from '../components/registers/lobby-form';
import { when } from '../components/scrapers/monitor-status';
import { Button } from '../components/ui/button';
import { useOptionalConfirm } from '../components/ui/confirm';
import { ApiError, api } from '../lib/api';
import { CONNECTORS_KEY, type ConnectorList, connectorKey } from '../lib/registers';

// A public-register connector: what it fetches, the matches to check (a person the name alone could not place:
// say who it is, or have them created), and its recent runs with their notes; Fetch now, change, pause, remove.

export const Route = createFileRoute('/admin/registers/$id')({ component: ConnectorPage });

function Matches({ connectorId, pending, onDone }: { connectorId: string; pending: PendingMatch[]; onDone: (text: string) => void }) {
  const { withReauth } = useReauth();
  const [error, setError] = useState<string | null>(null);
  const decide = useMutation({
    mutationFn: (b: { register: string; externalId: string; entityId?: string; create?: true; label: string }) =>
      withReauth(() => api(`/admin/registers/${connectorId}/matches`, { body: b })),
    onSuccess: (_r, b) => {
      setError(null);
      onDone(
        b.create
          ? `${b.label} will be created as a new entity at the next run, with their records.`
          : `${b.label} is linked. Their records are written at the next run.`,
      );
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
      setError(e instanceof ApiError ? e.message : 'Something went wrong.');
    },
  });
  if (!pending.length)
    return (
      <EmptyState icon={UserSearch} title="Nothing to check" learnMore="public-registers">
        People and bodies are matched by their register id, else by an exact name. When only a similar name exists, they wait here.
      </EmptyState>
    );
  return (
    <>
      {error && (
        <p role="alert" className="text-sm text-danger-foreground">
          {error}
        </p>
      )}
      <ul className="space-y-3" aria-label="Matches to check">
        {pending.map(p => (
          <li
            key={`${p.register}:${p.externalId}`}
            className="space-y-2 rounded-lg border bg-surface p-3 text-sm"
            data-testid="register-match"
          >
            <p>
              <span className="font-medium">
                <DataText>{p.label}</DataText>
              </span>{' '}
              <span className="text-xs text-muted-foreground">
                ({p.register === 'ep_mep' ? 'EP id' : 'register id'} <DataNumber>{p.externalId}</DataNumber>) may already be in Hoopoe as:
              </span>
            </p>
            <ul className="space-y-1">
              {p.candidates.map(c => (
                <li key={c.entityId} className="flex flex-wrap items-center gap-2">
                  <Link to="/entity/$id" params={{ id: c.entityId }} className="underline underline-offset-2">
                    <DataText>{c.displayName}</DataText>
                  </Link>
                  <span className="text-xs text-muted-foreground">
                    certainty <DataNumber>{Math.round(c.score * 100)}%</DataNumber>
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={decide.isPending}
                    context={`— ${p.label} is ${c.displayName}`}
                    onClick={() => decide.mutate({ register: p.register, externalId: p.externalId, entityId: c.entityId, label: p.label })}
                  >
                    Same person
                  </Button>
                </li>
              ))}
            </ul>
            <Button
              size="sm"
              variant="ghost"
              disabled={decide.isPending}
              onClick={() => decide.mutate({ register: p.register, externalId: p.externalId, create: true, label: p.label })}
            >
              None of these: create {p.label}
            </Button>
          </li>
        ))}
      </ul>
    </>
  );
}

const counts = (r: RegisterRunSummary) =>
  [
    `${r.stats.fetched.toLocaleString()} read`,
    `${r.stats.claims.toLocaleString()} claims`,
    `${r.stats.entitiesCreated} created`,
    `${r.stats.entitiesMatched} matched`,
    r.stats.known ? `${r.stats.known.toLocaleString()} already in` : null,
    r.stats.toReview ? `${r.stats.toReview} to check` : null,
    r.stats.failed ? `${r.stats.failed} failed` : null,
  ]
    .filter(Boolean)
    .join(' · ');

function ConnectorPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { withReauth } = useReauth();
  const confirm = useOptionalConfirm();
  const q = useQuery({ queryKey: connectorKey(id), queryFn: () => api<RegisterConnectorDetail>(`/admin/registers/${id}`) });
  const list = useQuery({ queryKey: CONNECTORS_KEY, queryFn: () => api<ConnectorList>('/admin/registers') });
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const onError = (e: unknown) => {
    if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
    setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : 'Something went wrong.' });
  };
  const refresh = () => {
    qc.invalidateQueries({ queryKey: connectorKey(id) });
    qc.invalidateQueries({ queryKey: CONNECTORS_KEY, exact: true });
  };
  const run = useMutation({
    mutationFn: () => withReauth(() => api<{ run: RegisterRunSummary }>(`/admin/registers/${id}/run`, { body: {} })),
    onMutate: () => setMessage(null),
    onSuccess: () =>
      setMessage({ kind: 'ok', text: 'Fetching. Progress shows on your activity card; the results appear below when it finishes.' }),
    onSettled: refresh,
    onError,
  });
  const save = useMutation({
    mutationFn: (input: RegisterConnectorInput & { enabled?: boolean }) =>
      withReauth(() => api<{ connector: RegisterConnectorSummary }>(`/admin/registers/${id}`, { method: 'PATCH', body: input })),
    onSuccess: (r, input) => {
      setEditing(false);
      setMessage({
        kind: 'ok',
        text:
          input.enabled === false
            ? `Paused “${r.connector.name}”. It doesn’t run on schedule until you resume it.`
            : input.enabled === true
              ? `Resumed “${r.connector.name}”.`
              : `Saved “${r.connector.name}”. The changes apply from its next run.`,
      });
      refresh();
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: () => withReauth(() => api(`/admin/registers/${id}`, { method: 'DELETE' })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CONNECTORS_KEY });
      navigate({ to: '/admin/registers' });
    },
    onError,
  });

  if (q.isError)
    return (
      <>
        <PageHeader title="Register connector" />
        <ErrorState error={q.error} title="This connector couldn’t be loaded" onRetry={() => q.refetch()} learnMore="public-registers" />
      </>
    );
  if (!q.data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const { connector: c, runs, pending } = q.data;
  const switchedOff = list.data?.settings.enabled === false;
  const s = c.settings;

  const edit = (input: RegisterConnectorInput) => {
    setMessage(null);
    save.mutate(input);
  };

  const askRemove = async () => {
    setMessage(null);
    const ok = confirm
      ? await confirm({
          title: `Remove “${c.name}”?`,
          body: 'It stops fetching. The entities and claims it wrote stay.',
          confirmLabel: 'Remove connector',
          cancelLabel: 'Keep it',
          destructive: true,
        })
      : true;
    if (ok) remove.mutate();
  };

  return (
    <div className="space-y-8">
      <Link to="/admin/registers" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" aria-hidden="true" /> All public registers
      </Link>
      <PageHeader title={c.name} description={`${SCHEDULE_LABELS[c.schedule]} · ${describeSettings(s)}`} />
      <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="connector-status">
        <ConnectorStatus connector={c} />
        <span className="text-muted-foreground">
          · Next run: <DataNumber>{c.enabled ? (c.nextRunAt ? when(c.nextRunAt) : 'by hand only') : 'paused'}</DataNumber>
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button disabled={run.isPending || c.running || switchedOff} onClick={() => run.mutate()}>
          {c.running ? 'Fetching…' : 'Fetch now'}
        </Button>
        <Button variant="outline" onClick={() => setEditing(e => !e)} aria-expanded={editing}>
          Change settings
        </Button>
        <Button variant="outline" disabled={save.isPending} onClick={() => save.mutate({ enabled: !c.enabled })}>
          {c.enabled ? 'Pause' : 'Resume'}
        </Button>
        <Button variant="ghost" onClick={askRemove} disabled={remove.isPending}>
          Remove
        </Button>
      </div>
      {switchedOff && (
        <p role="status" className="rounded-md border border-warning bg-surface px-3 py-2 text-sm">
          Public registers are switched off for everyone. Switch them on in Admin → Public registers to fetch.
        </p>
      )}
      {message && (
        <p
          role={message.kind === 'error' ? 'alert' : 'status'}
          className={message.kind === 'error' ? 'text-sm text-danger-foreground' : 'text-sm'}
        >
          {message.text}
        </p>
      )}

      {editing && (
        <section aria-labelledby="edit-title" className="space-y-3">
          <h2 id="edit-title" className="font-semibold">
            Change “<DataText>{c.name}</DataText>”
          </h2>
          {c.kind === 'lobby_funding' ? (
            <LobbyForm connector={c} titleId="edit-title" pending={save.isPending} onSubmit={edit} onCancel={() => setEditing(false)} />
          ) : (
            <ConnectorForm connector={c} titleId="edit-title" pending={save.isPending} onSubmit={edit} onCancel={() => setEditing(false)} />
          )}
        </section>
      )}

      <section aria-labelledby="what-title" className="space-y-2 text-sm">
        <h2 id="what-title" className="font-semibold">
          What it fetches
        </h2>
        {isLobbySettings(s) ? <LobbyDetails s={s} /> : <EpDetails s={s} />}
      </section>

      <section aria-labelledby="matches-title" className="space-y-3">
        <h2 id="matches-title" className="font-semibold">
          Matches to check
        </h2>
        <Matches
          connectorId={id}
          pending={pending}
          onDone={text => {
            setMessage({ kind: 'ok', text });
            refresh();
          }}
        />
      </section>

      <section aria-labelledby="runs-title" className="space-y-3">
        <h2 id="runs-title" className="font-semibold">
          Recent runs
        </h2>
        {runs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No run yet.</p>
        ) : (
          <ol className="space-y-3" aria-label="Recent runs">
            {runs.map(r => (
              <li key={r.id} className="space-y-1 rounded-lg border bg-surface p-3 text-sm" data-testid="register-run">
                <p>
                  <span className={r.status === 'failed' ? 'font-medium text-danger-foreground' : 'font-medium'}>
                    {RUN_LABEL[r.status]}
                  </span>{' '}
                  <span className="text-xs text-muted-foreground">
                    <DataNumber>{when(r.startedAt)}</DataNumber> · {r.trigger === 'manual' ? 'by hand' : 'on schedule'}
                  </span>
                </p>
                <p>
                  <DataNumber>{counts(r)}</DataNumber>
                </p>
                {r.error && (
                  <p className="text-xs text-danger-foreground">
                    <DataText>{r.error}</DataText>
                  </p>
                )}
                {r.issues.length > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-muted-foreground">
                      <DataNumber>{r.issues.length}</DataNumber> {r.issues.length === 1 ? 'note' : 'notes'}
                    </summary>
                    <ul className="mt-1 list-disc space-y-0.5 ps-5">
                      {r.issues.map(i => (
                        <li key={i}>
                          <DataText>{i}</DataText>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </li>
            ))}
          </ol>
        )}
        <LearnMore slug="public-registers" />
      </section>
    </div>
  );
}
